// src/components/chat/FileUploadCarousel.tsx

import * as React from "react";
import { useRef, useState, useEffect } from "react";
import { fileUploadStyles, mergeStyles } from "./styles/fileUploadStyles";
import {
  truncateFileName,
  isWordDocument,
  isExcelDocument,
  isPDFDocument,
} from "../../utils/fileUtils";
import { createLogger } from "../../utils/logger";

// Import file type icons
import WordIcon from "../../../assets/Word.svg";
import ExcelIcon from "../../../assets/Excel.svg";
import PDFIcon from "../../../assets/PDF.svg";
import FileUploadingIcon from "../../../assets/FileUploading.svg";

const log = createLogger("FileUploadCarousel");

/**
 * Represents a file in the upload carousel
 * Extends the native File type with upload state tracking
 */
export interface UploadedFile {
  file: File;
  id: string;
  isUploading: boolean;
  uploadProgress?: number;
  error?: string;
  // SharePoint metadata (preserved from File object)
  isSharePointFile?: boolean;
  sharePointWebUrl?: string | null;
}

/**
 * Props for the FileUploadCarousel component
 */
interface FileUploadCarouselProps {
  files: UploadedFile[];
  onRemoveFile: (fileId: string) => void;
  error?: string;
  className?: string;
}

/**
 * FileUploadCarousel Component
 *
 * Displays uploaded files in a horizontal scrollable carousel with:
 * - File type icons (Word/Excel)
 * - Truncated file names
 * - Remove buttons
 * - Loading states during upload
 * - Scroll navigation arrows
 *
 * This component follows the same pattern as PromptSuggestionCarousel
 * for consistency in the UI.
 */
const FileUploadCarousel: React.FC<FileUploadCarouselProps> = ({
  files,
  onRemoveFile,
  error,
  className = "",
}) => {
  // Reference to the scroll container
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // State to track scroll position for button visibility
  const [scrollPosition, setScrollPosition] = useState(0);
  const [maxScroll, setMaxScroll] = useState(0);
  const [hoveredButton, setHoveredButton] = useState<"prev" | "next" | null>(null);
  const [hoveredRemoveButton, setHoveredRemoveButton] = useState<string | null>(null);
  const [hoveredFileItem, setHoveredFileItem] = useState<string | null>(null);

  // Calculate maximum scroll width on mount and when files change
  useEffect(() => {
    const updateScrollMetrics = () => {
      const container = scrollContainerRef.current;
      if (container) {
        // Calculate the maximum possible scroll (scrollWidth - visible width)
        const newMaxScroll = container.scrollWidth - container.clientWidth;
        setMaxScroll(newMaxScroll > 0 ? newMaxScroll : 0);
        log.debug("Updated scroll metrics", {
          scrollWidth: container.scrollWidth,
          clientWidth: container.clientWidth,
          maxScroll: newMaxScroll,
        });
      }
    };

    // Initial calculation
    updateScrollMetrics();

    // Recalculate on window resize
    window.addEventListener("resize", updateScrollMetrics);
    return () => window.removeEventListener("resize", updateScrollMetrics);
  }, [files]); // Recalculate when files change

  // Handle scroll events to update button visibility
  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      setScrollPosition(container.scrollLeft);
    }
  };

  // Scroll left/right by a fixed amount
  const scrollBy = (amount: number) => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({ left: amount, behavior: "smooth" });
    }
  };

  // Get the appropriate icon for a file
  const getFileIcon = (uploadedFile: UploadedFile) => {
    if (uploadedFile.isUploading) {
      // Scale down the FileUploading icon to match other icons
      return (
        <div
          style={{
            width: "50px",
            height: "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transform: "scale(0.9)", // Adjust this value as needed
            transformOrigin: "center",
          }}
        >
          <FileUploadingIcon />
        </div>
      );
    }

    if (isWordDocument(uploadedFile.file)) {
      return <WordIcon />;
    }

    if (isExcelDocument(uploadedFile.file)) {
      return <ExcelIcon />;
    }

    if (isPDFDocument(uploadedFile.file)) {
      return <PDFIcon />;
    }

    // Fallback - shouldn't happen with proper validation
    return <FileUploadingIcon />;
  };

  // Handle file removal with logging
  const handleRemoveFile = (fileId: string, fileName: string) => {
    log.info("Removing file", { fileId, fileName });
    onRemoveFile(fileId);
  };

  // Handle SharePoint file click to open in browser
  const handleSharePointFileClick = (uploadedFile: UploadedFile) => {
    if (uploadedFile.isSharePointFile && uploadedFile.sharePointWebUrl) {
      log.info("Opening SharePoint file in browser", {
        fileId: uploadedFile.id,
        fileName: uploadedFile.file.name,
        webUrl: uploadedFile.sharePointWebUrl,
      });
      window.open(uploadedFile.sharePointWebUrl, "_blank", "noopener,noreferrer");
    }
  };

  // Render empty state if no files
  if (files.length === 0 && !error) {
    return null; // Don't show anything if no files are uploaded
  }

  return (
    <div className={className}>
      {/* Error message display */}
      {error && <div style={fileUploadStyles.errorMessage}>{error}</div>}

      {/* Main carousel container */}
      {files.length > 0 && (
        <div style={fileUploadStyles.carouselContainer}>
          {/* Previous button */}
          <button
            style={mergeStyles(
              fileUploadStyles.navButtonBase,
              fileUploadStyles.prevButton,
              scrollPosition > 0
                ? fileUploadStyles.navButtonVisible
                : fileUploadStyles.navButtonHidden,
              hoveredButton === "prev" && fileUploadStyles.navButtonHover
            )}
            onClick={() => scrollBy(-200)}
            onMouseEnter={() => setHoveredButton("prev")}
            onMouseLeave={() => setHoveredButton(null)}
            aria-label="Previous files"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 18L9 12L15 6"
                stroke="#333"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {/* Scrollable container with files */}
          <div
            ref={scrollContainerRef}
            style={fileUploadStyles.scrollContainer}
            onScroll={handleScroll}
          >
            {files.map((uploadedFile) => {
              const isClickable = uploadedFile.isSharePointFile && !!uploadedFile.sharePointWebUrl;
              const isHovered = hoveredFileItem === uploadedFile.id;

              return (
                <div
                  key={uploadedFile.id}
                  style={mergeStyles(
                    fileUploadStyles.fileItem,
                    uploadedFile.isUploading && fileUploadStyles.fileItemLoading,
                    isClickable && {
                      cursor: "pointer",
                      transition: "all 0.2s",
                    },
                    isClickable &&
                      isHovered && {
                        backgroundColor: "#f0f7ff",
                        transform: "translateY(-1px)",
                        boxShadow: "0 2px 6px rgba(0, 102, 204, 0.15)",
                      }
                  )}
                  onClick={() => isClickable && handleSharePointFileClick(uploadedFile)}
                  onMouseEnter={() => isClickable && setHoveredFileItem(uploadedFile.id)}
                  onMouseLeave={() => setHoveredFileItem(null)}
                  title={
                    isClickable
                      ? `Click to view in SharePoint: ${uploadedFile.file.name}`
                      : uploadedFile.file.name
                  }
                >
                  {/* File icon */}
                  <div style={fileUploadStyles.fileIconContainer}>{getFileIcon(uploadedFile)}</div>

                  {/* File name and context text */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    <span
                      style={fileUploadStyles.fileName}
                      title={uploadedFile.file.name} // Show full name on hover
                    >
                      {truncateFileName(uploadedFile.file.name, 25)}
                    </span>
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        fontFamily: "'Segoe UI', sans-serif",
                        lineHeight: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      {uploadedFile.isUploading
                        ? "Uploading..."
                        : uploadedFile.isSharePointFile
                          ? "Library Document"
                          : "Context Document"}
                      {/* External link icon for SharePoint files */}
                      {uploadedFile.isSharePointFile && uploadedFile.sharePointWebUrl && (
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          style={{ flexShrink: 0 }}
                        >
                          <path
                            d="M18 13V19C18 19.5304 17.7893 20.0391 17.4142 20.4142C17.0391 20.7893 16.5304 21 16 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V8C3 7.46957 3.21071 6.96086 3.58579 6.58579C3.96086 6.21071 4.46957 6 5 6H11M15 3H21M21 3V9M21 3L10 14"
                            stroke="#0066cc"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </span>
                  </div>

                  {/* Remove button - only show if not uploading */}
                  {!uploadedFile.isUploading && (
                    <button
                      style={mergeStyles(
                        fileUploadStyles.removeButton,
                        hoveredRemoveButton === uploadedFile.id &&
                          fileUploadStyles.removeButtonHover
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFile(uploadedFile.id, uploadedFile.file.name);
                      }}
                      onMouseEnter={() => setHoveredRemoveButton(uploadedFile.id)}
                      onMouseLeave={() => setHoveredRemoveButton(null)}
                      aria-label={`Remove ${uploadedFile.file.name}`}
                      title="Remove file"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M18 6L6 18M6 6L18 18"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Next button */}
          <button
            style={mergeStyles(
              fileUploadStyles.navButtonBase,
              fileUploadStyles.nextButton,
              scrollPosition < maxScroll
                ? fileUploadStyles.navButtonVisible
                : fileUploadStyles.navButtonHidden,
              hoveredButton === "next" && fileUploadStyles.navButtonHover
            )}
            onClick={() => scrollBy(200)}
            onMouseEnter={() => setHoveredButton("next")}
            onMouseLeave={() => setHoveredButton(null)}
            aria-label="Next files"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 18L15 12L9 6"
                stroke="#333"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default FileUploadCarousel;
